import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../contexts/AuthContext';
import { LogIn, Home, ShieldCheck, ArrowRight, KeyRound, QrCode } from 'lucide-react';
import PublicLanguageSwitcher from '../../components/PublicLanguageSwitcher';
import { savePlanUpgradeContext, clearPlanUpgradeContext } from '../../utils/planUpgrade';
import { apiErrorText } from '../../utils/api';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [demoNotice, setDemoNotice] = useState<{ title: string; subtitle: string; highlights: string[] } | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totpRequired, setTotpRequired] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [totpQrUri, setTotpQrUri] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [showQr, setShowQr] = useState(false);
  const totpInputRef = useRef<HTMLInputElement>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (totpRequired && totpInputRef.current) {
      totpInputRef.current.focus();
    }
  }, [totpRequired]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setDemoNotice(null);
    setRedirecting(false);
    setLoading(true);

    try {
      const loggedInUser = await login(email, password, totpRequired ? totpCode : undefined);
      setTotpRequired(false);
      setTotpCode('');
      setTotpQrUri('');
      setTotpSecret('');
      setShowQr(false);
      if (loggedInUser.role === 'admin') {
        navigate('/app/admin');
      } else {
        navigate('/app/dashboard');
      }
    } catch (err: any) {
      // Check if 2FA code is required
      const detail = apiErrorText(err.response?.data?.detail);
      const detailObj = typeof detail === 'object' && detail !== null ? detail : {};
      if (err.response?.status === 401 && detailObj.code === 'totp_required') {
        setTotpRequired(true);
        setTotpQrUri(detailObj.qr_uri || '');
        setTotpSecret(detailObj.secret || '');
        setShowQr(false);
        setError('');
        setLoading(false);
        return;
      }

      // Check if demo period expired (HTTP 402)
      if (err.response?.status === 402) {
        const detail402 = apiErrorText(err.response?.data?.detail) || {};

        if (detail402?.code === 'payment_required' && detail402?.payment_link) {
          const enterpriseTitle = t('auth.login.enterprisePaymentTitle');
          const enterpriseSubtitle = t(
            'auth.login.enterprisePaymentSubtitle');
          const highlights = [
            t('auth.login.enterprisePaymentHighlight1'),
            t('auth.login.enterprisePaymentHighlight2'),
            t('auth.login.enterprisePaymentHighlight3')
          ];
          setDemoNotice({ title: enterpriseTitle, subtitle: enterpriseSubtitle, highlights });
          setRedirecting(true);
          setTimeout(() => {
            window.location.href = detail402.payment_link;
          }, 3000);
          return;
        }

        const planUpgradeToken = detail402?.plan_upgrade_token;
        const tenantInfo = detail402?.tenant;

        if (planUpgradeToken) {
          savePlanUpgradeContext({
            token: planUpgradeToken,
            tenantSlug: tenantInfo?.slug,
            tenantId: tenantInfo?.id,
            tenantName: tenantInfo?.name,
            email: email
          });
        } else {
          clearPlanUpgradeContext();
        }

        const isSubscriptionCancelled = detail402?.reason === 'subscription_cancelled';
        const title = isSubscriptionCancelled
          ? t('auth.login.subscriptionCancelledTitle')
          : t('auth.login.demoExpiredTitle');
        const subtitle = isSubscriptionCancelled
          ? t(
              'auth.login.subscriptionCancelledSubtitle')
          : t(
              'auth.login.demoExpiredSubtitle');
        const highlights = isSubscriptionCancelled
          ? [
              t('auth.login.subscriptionCancelledHighlight1'),
              t('auth.login.subscriptionCancelledHighlight2'),
              t('auth.login.subscriptionCancelledHighlight3')
            ]
          : [
              t('auth.login.demoExpiredHighlight1'),
              t('auth.login.demoExpiredHighlight2'),
              t('auth.login.demoExpiredHighlight3')
            ];
        setDemoNotice({ title, subtitle, highlights });
        setRedirecting(true);
        setTimeout(() => {
          navigate('/plan-upgrade', {
            replace: true,
            state: {
              demoExpired: !isSubscriptionCancelled,
              subscriptionCancelled: isSubscriptionCancelled,
              email: email,
              message: isSubscriptionCancelled
                ? t('auth.login.subscriptionCancelledMessage')
                : t('auth.login.demoExpired')
            }
          });
        }, 3500);
        return;
      }

      const errMsg = err?.response?.data?.detail?.message
        || apiErrorText(err?.response?.data?.detail)
        || (typeof err?.message === 'string' ? err.message : null)
        || t('auth.login.error');
      setError(String(errMsg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-lotus-cream via-white to-lotus-gold/10 flex items-center justify-center p-4 relative">
      <div className="absolute top-6 left-6">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-lotus-gold transition-colors"
        >
          <Home size={18} />
          <span>{t('auth.backToHome')}</span>
        </Link>
      </div>
      <div className="absolute top-6 right-6">
        <PublicLanguageSwitcher />
      </div>
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8 fade-in">
          <Link to="/" className="inline-block mb-4 hover:opacity-80 transition-opacity">
            <img 
              src="/logo.png" 
              alt="LotusRecruiter" 
              className="w-60 h-60 mx-auto object-contain"
            />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.login.title')}</h1>
          <p className="text-gray-600">{t('auth.login.subtitle')}</p>
        </div>

        {/* Login Card */}
        <div className="card fade-in">
          {demoNotice ? (
            <div className="p-6 text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-lotus-gold/15 flex items-center justify-center">
                <ShieldCheck className="text-lotus-gold" size={28} />
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">{demoNotice.title}</p>
                <p className="text-sm text-gray-700 mt-2">{demoNotice.subtitle}</p>
                <ul className="mt-4 text-sm text-gray-700 space-y-2 list-disc list-inside text-left">
                  {demoNotice.highlights.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
              {redirecting && (
                <p className="mt-2 text-sm font-medium text-lotus-gold flex items-center justify-center gap-2">
                  <ArrowRight size={16} />
                  {t('auth.login.demoRedirecting')}
                </p>
              )}
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="label">{t('auth.login.email')}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    placeholder={t('auth.login.emailPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className="label">{t('auth.login.password')}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input"
                    placeholder={t('auth.login.passwordPlaceholder')}
                    required
                  />
                </div>

                {totpRequired && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                    <div className="flex items-center gap-2 text-blue-700">
                      <KeyRound size={18} />
                      <span className="text-sm font-medium">
                        {t('auth.login.totpRequired')}
                      </span>
                    </div>
                    <p className="text-xs text-blue-600">
                      {t('auth.login.totpHint')}
                    </p>
                    <input
                      ref={totpInputRef}
                      type="text"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="input text-center text-2xl tracking-widest font-mono"
                      placeholder="000000"
                      maxLength={6}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                    />
                    <div className="text-xs text-blue-600 space-y-1 pt-1">
                      <p>
                        {t('auth.login.totpHelp1')}
                      </p>
                      <p>
                        {t('auth.login.totpHelp2')}
                      </p>
                      <p className="pt-1">
                        {t('auth.login.totpHelp3')}
                      </p>
                    </div>

                    {totpQrUri && (
                      <div className="pt-2 border-t border-blue-200">
                        <button
                          type="button"
                          onClick={() => setShowQr(!showQr)}
                          className="flex items-center gap-2 text-sm text-blue-700 hover:text-blue-800 font-medium"
                        >
                          <QrCode size={16} />
                          {showQr
                            ? t('auth.login.hideQr')
                            : t('auth.login.showQr')}
                        </button>

                        {showQr && (
                          <div className="mt-3 p-4 bg-white rounded-lg border border-blue-200 text-center">
                            <p className="text-xs text-blue-600 mb-3">
                              {t('auth.login.qrScanHint')}
                            </p>
                            <div className="flex justify-center">
                              <QRCodeSVG
                                value={totpQrUri}
                                size={180}
                                level="M"
                                includeMargin={true}
                              />
                            </div>
                            {totpSecret && (
                              <div className="mt-3">
                                <p className="text-xs text-gray-500 mb-1">
                                  {t('auth.login.manualEntry')}
                                </p>
                                <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono break-all">
                                  {totpSecret}
                                </code>
                              </div>
                            )}
                            <p className="text-xs text-amber-600 mt-3">
                              {t('auth.login.qrSecurityWarning')}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    <>
                      <LogIn size={20} />
                      <span>{t('auth.login.submit')}</span>
                    </>
                  )}
                </button>
              </form>

              <div className="mt-4 text-center">
                <Link to="/reset-password" className="text-sm text-lotus-gold hover:text-lotus-gold-dark font-medium">
                  {t('auth.login.forgotPassword')}
                </Link>
              </div>

              <div className="mt-6 text-center space-y-3">
                <p className="text-sm text-gray-600">
                  {t('auth.login.noAccount')}{' '}
                  <Link to="/onboarding" className="text-lotus-gold hover:text-lotus-gold-dark font-medium">
                    {t('auth.login.signUp')}
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>{t('landing.footer.copyright')}</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
